/**
 * Gigglz: Intercept checkout and route to Delivery Wizard
 * Works across desktop + mobile (incl. iOS Safari) by:
 * - capturing the last clicked/touched checkout control (fallback when e.submitter is missing)
 * - intercepting both submit + click-to-/checkout patterns
 *
 * Send List baskets: lines added from a Send List page already carry full
 * delivery details (_gigglz_send_list_id). If every line in the basket is a
 * list line, skip the wizard and go straight to checkout. Mixed baskets still
 * go through the wizard, which ignores the list lines.
 */
(function () {
  // Change this if your wizard URL changes
  const WIZARD_URL = '/pages/delivery';
  const CHECKOUT_URL = '/checkout';

  let routing = false;

  function goWizard() {
    // Avoid loops if something ever renders this on the wizard page
    if (window.location.pathname === WIZARD_URL) return;
    window.location.href = WIZARD_URL;
  }

  function isListItem(item) {
    const p = (item && item.properties) || {};
    return String(p._gigglz_send_list_id || '').trim() !== '';
  }

  // List-only baskets: prefill the checkout address from the buyer's own
  // "send to me" address, when there is one and the customer isn't signed in
  // (signed-in customers get their saved address from Shopify).
  function listCheckoutUrl(items) {
    try {
      if (window.gigglzCustomerLoggedIn) return CHECKOUT_URL;
      const me = items.map(i => i.properties || {}).filter(p => p._gigglz_send_to === 'me' && p._gigglz_address1 && p._gigglz_city && p._gigglz_postcode && p._gigglz_full_name);
      if (!me.length) return CHECKOUT_URL;
      const key = p => [p._gigglz_full_name, p._gigglz_address1, p._gigglz_address2, p._gigglz_city, p._gigglz_postcode].map(v => String(v || '').trim().toLowerCase()).join('|');
      if (!me.every(p => key(p) === key(me[0]))) return CHECKOUT_URL;
      const a = me[0];
      const parts = String(a._gigglz_full_name).trim().split(/\s+/).filter(Boolean);
      const params = new URLSearchParams();
      if (parts.length > 1) params.set('checkout[shipping_address][first_name]', parts.slice(0, -1).join(' '));
      params.set('checkout[shipping_address][last_name]', parts[parts.length - 1] || '');
      params.set('checkout[shipping_address][address1]', a._gigglz_address1);
      if (a._gigglz_address2) params.set('checkout[shipping_address][address2]', a._gigglz_address2);
      params.set('checkout[shipping_address][city]', a._gigglz_city);
      if (a._gigglz_county) params.set('checkout[shipping_address][province]', a._gigglz_county);
      params.set('checkout[shipping_address][country]', 'United Kingdom');
      params.set('checkout[shipping_address][zip]', a._gigglz_postcode);
      return CHECKOUT_URL + '?' + params.toString();
    } catch (e) {
      return CHECKOUT_URL;
    }
  }

  // Decide where checkout should go. Any failure falls back to the wizard,
  // which is the existing behaviour.
  async function routeCheckout() {
    if (routing) return;
    routing = true;
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) throw new Error('cart ' + res.status);
      const cart = await res.json();
      const items = Array.isArray(cart.items) ? cart.items : [];

      if (items.length > 0 && items.every(isListItem)) {
        window.location.href = listCheckoutUrl(items);
        return;
      }
    } catch (e) {
      // fall through to the wizard
    }
    routing = false;
    goWizard();
  }

  function shouldIntercept() {
    // If you want to scope this later (cart only, etc.), do it here.
    // For now: intercept anywhere in the theme.
    return true;
  }

  function isCheckoutControl(el) {
    if (!el) return false;

    // Dawn commonly uses button[name="checkout"] or input[name="checkout"]
    if (el.matches && el.matches('button[name="checkout"], input[name="checkout"]')) return true;

    // Some themes use an <a href="/checkout"> as the button
    if (el.tagName === 'A') {
      const href = (el.getAttribute('href') || '').trim();
      if (href === '/checkout' || href.startsWith('/checkout?')) return true;
    }

    // Some themes add data attributes/classes (best-effort)
    const href2 = (el.getAttribute && el.getAttribute('href')) ? el.getAttribute('href').trim() : '';
    if (href2 === '/checkout' || href2.startsWith('/checkout?')) return true;

    return false;
  }

  // --- Mobile-safe submitter fallback ---
  let lastCheckoutControl = null;

  function capturePotentialCheckoutTarget(e) {
    const t = e.target && e.target.closest ? e.target.closest('a, button, input') : null;
    if (!t) return;
    if (isCheckoutControl(t)) lastCheckoutControl = t;
  }

  // Capture early for touch devices
  document.addEventListener('touchstart', capturePotentialCheckoutTarget, true);
  document.addEventListener('pointerdown', capturePotentialCheckoutTarget, true);
  document.addEventListener('mousedown', capturePotentialCheckoutTarget, true);
  document.addEventListener('click', capturePotentialCheckoutTarget, true);

  // 1) Intercept form submit when it's a checkout submit
  document.addEventListener(
    'submit',
    function (e) {
      if (!shouldIntercept()) return;

      // Prefer native submitter if present, fallback to our captured control.
      const submitter = e.submitter || lastCheckoutControl || document.activeElement;

      if (submitter && isCheckoutControl(submitter)) {
        e.preventDefault();
        e.stopPropagation();
        routeCheckout();
      }
    },
    true // capture
  );

  // 2) Intercept direct /checkout links (including odd cases not involving submit)
  document.addEventListener(
    'click',
    function (e) {
      if (!shouldIntercept()) return;

      const el = e.target && e.target.closest ? e.target.closest('a, button, input') : null;
      if (!el) return;

      // If they tapped a /checkout link, route via the basket check
      if (el.tagName === 'A') {
        const href = (el.getAttribute('href') || '').trim();
        if (href === '/checkout' || href.startsWith('/checkout?')) {
          e.preventDefault();
          e.stopPropagation();
          routeCheckout();
        }
      }
    },
    true
  );

  // Lets other scripts (e.g. the delivery wizard) reuse the same rule.
  window.GigglzCheckoutRoute = { isListItem: isListItem, routeCheckout: routeCheckout };
})();
